import React, { useState, useEffect } from 'react';
import T from '../../../components/common/T';
import { TrashIcon, MapPinIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { toast } from "react-toastify";

export default function ConstructionTab({ project, onUpdate, readOnly }) {
  
  // 🟢 1. LOCAL BUFFER STATE (Holds data while typing)
  const [formData, setFormData] = useState({
    constructionType: "Residential",
    email: "",
    phone: "",
    address: "",
    geo: { lat: "", lng: "" },
    siteTitle: "",
    siteLength: 0,
    siteWidth: 0,
    siteArea: 0,
    subUnits: []
  });

  // 🟢 2. SYNC ON LOAD (Only runs once when project loads)
  useEffect(() => {
    if (project) {
      setFormData({
        constructionType: project.constructionType || "Residential",
        email: project.email || "",
        phone: project.phone || "",
        address: project.address || "",
        geo: project.geo || { lat: "", lng: "" },
        siteTitle: project.siteTitle || project.name || "",
        siteLength: project.siteLength || 0,
        siteWidth: project.siteWidth || 0,
        siteArea: project.siteArea || 0,
        subUnits: project.subUnits || []
      });
    }
  }, [project]);

  // --- HANDLERS (Update Local State Only) ---

  // General Field Update
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Nested Geo Update
  const handleGeoChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      geo: { ...prev.geo, [key]: value }
    }));
  };

  // Auto-Calculate Site Area
  const handleMeasurementChange = (field, value) => {
    const newVal = Number(value);
    
    setFormData(prev => {
      const updated = { ...prev, [field]: newVal };
      
      // Auto-calc area if width or length changes
      if (field === 'siteWidth' || field === 'siteLength') {
        const w = field === 'siteWidth' ? newVal : Number(prev.siteWidth);
        const l = field === 'siteLength' ? newVal : Number(prev.siteLength);
        updated.siteArea = w * l;
      }
      return updated;
    });
  };

  // Sub-Unit Table Logic
  const addLine = () => {
    const newLine = {
      id: Date.now(),
      name: "",
      qty: 1,
      uom: "Units",
      preCost: 0,
      underCost: 0,
      postCost: 0
    };
    setFormData(prev => ({ ...prev, subUnits: [...prev.subUnits, newLine] }));
  };

  const updateLine = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      subUnits: prev.subUnits.map(item => item.id === id ? { ...item, [field]: value } : item)
    }));
  };

  const removeLine = (id) => {
    setFormData(prev => ({
      ...prev,
      subUnits: prev.subUnits.filter(item => item.id !== id)
    }));
  };

  // 🟢 3. SAVE HANDLER (The only place onUpdate is called)
  const handleSave = () => {
    onUpdate({
      ...project, // Keep original ID and other fields safe
      ...formData // Overwrite with new site details
    });
    toast.success("Site details updated successfully!");
  };

  return (
    <div className="p-4 md:p-6 bg-white dark:bg-[#161b22] min-h-[500px] transition-colors">
      
      {/* 🟢 ACTION HEADER */}
      <div className="flex justify-between items-center mb-6 border-b border-gray-100 dark:border-[#30363d] pb-4">
        <h3 className="font-bold text-gray-700 dark:text-[#f0f6fc] text-sm md:text-base uppercase tracking-wide"><T>Site Configuration</T></h3>
        {!readOnly && (
            <button 
                onClick={handleSave}
                className="bg-[#D9531E] hover:bg-[#C24514] active:scale-[0.98] text-white px-5 py-2 rounded-lg text-xs font-bold uppercase shadow-sm flex items-center gap-2 transition"
            >
                <ArrowDownTrayIcon className="w-4 h-4" /> <T>Update Site Info</T>
            </button>
        )}
      </div>
      
      {/* --- SECTION 1: SITE DETAILS (2 Columns) --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 mb-8">
        
        {/* LEFT COL: Basic Info */}
        <div className="space-y-4">
          <div className="flex items-center">
            <label className="w-40 text-sm font-bold text-gray-700 dark:text-[#848d97]"><T>Type of Construction</T></label>
            <select 
              disabled={readOnly}
              className="flex-1 border-b border-gray-300 dark:border-[#30363d] focus:border-[#D9531E] outline-none text-sm py-1 bg-transparent text-gray-900 dark:text-white"
              value={formData.constructionType}
              onChange={(e) => handleChange("constructionType", e.target.value)}
            >
              <option value="Residential" className="dark:bg-[#161b22]"><T>Residential</T></option>
              <option value="Commercial" className="dark:bg-[#161b22]"><T>Commercial</T></option>
              <option value="Industrial" className="dark:bg-[#161b22]"><T>Industrial</T></option>
              <option value="Mixed" className="dark:bg-[#161b22]"><T>Mixed Utility</T></option>
            </select>
          </div>

          <div className="flex items-center">
            <label className="w-40 text-sm font-bold text-gray-700 dark:text-[#848d97]"><T>Email</T></label>
            <input 
              type="email" 
              disabled={readOnly}
              className="flex-1 border-b border-gray-300 dark:border-[#30363d] focus:border-[#D9531E] outline-none text-sm py-1 bg-transparent text-gray-900 dark:text-white"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder="site@example.com"
            />
          </div>

          <div className="flex items-center">
            <label className="w-40 text-sm font-bold text-gray-700 dark:text-[#848d97]"><T>Phone</T></label>
            <input 
              type="text" 
              disabled={readOnly}
              className="flex-1 border-b border-gray-300 dark:border-[#30363d] focus:border-[#D9531E] outline-none text-sm py-1 bg-transparent text-gray-900 dark:text-white"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              placeholder="+91..."
            />
          </div>
        </div>

        {/* RIGHT COL: Location & Geo */}
        <div className="space-y-4">
          <div className="flex items-start">
            <label className="w-32 text-sm font-bold text-gray-700 dark:text-[#848d97] pt-1"><T>Address</T></label>
            <textarea 
              disabled={readOnly}
              className="flex-1 border-b border-gray-300 dark:border-[#30363d] focus:border-[#D9531E] outline-none text-sm py-1 resize-none bg-transparent text-gray-900 dark:text-white"
              rows="2"
              value={formData.address}
              onChange={(e) => handleChange("address", e.target.value)}
              placeholder="Site Address..."
            />
          </div>

          <div className="flex items-center">
            <label className="w-32 text-sm font-bold text-gray-700 dark:text-[#848d97]"><T>Longitude</T></label>
            <div className="flex-1 flex items-center border-b border-gray-300 dark:border-[#30363d]">
               <input 
                 type="text" 
                 disabled={readOnly}
                 className="flex-1 outline-none text-sm py-1 bg-transparent text-gray-900 dark:text-white"
                 value={formData.geo.lng}
                 onChange={(e) => handleGeoChange("lng", e.target.value)}
               />
               <MapPinIcon className="w-4 h-4 text-gray-400 dark:text-[#6e7681]" />
            </div>
          </div>

          <div className="flex items-center">
            <label className="w-32 text-sm font-bold text-gray-700 dark:text-[#848d97]"><T>Latitude</T></label>
            <div className="flex-1 flex items-center border-b border-gray-300 dark:border-[#30363d]">
               <input 
                 type="text" 
                 disabled={readOnly}
                 className="flex-1 outline-none text-sm py-1 bg-transparent text-gray-900 dark:text-white"
                 value={formData.geo.lat}
                 onChange={(e) => handleGeoChange("lat", e.target.value)}
               />
               <MapPinIcon className="w-4 h-4 text-gray-400 dark:text-[#6e7681]" />
            </div>
          </div>
        </div>
      </div>

      {/* --- SECTION 2: SITE MEASUREMENTS --- */}
      <h3 className="text-xs font-bold text-gray-500 dark:text-[#848d97] uppercase tracking-wider mb-4 border-b border-gray-100 dark:border-[#30363d] pb-2"><T>Site Measurement</T></h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 mb-8">
         <div className="space-y-4">
            <div className="flex items-center">
              <label className="w-40 text-sm font-bold text-gray-700 dark:text-[#848d97]"><T>Site Title</T></label>
              <input 
                disabled={readOnly}
                className="flex-1 border-b border-gray-300 dark:border-[#30363d] focus:border-[#D9531E] outline-none text-sm py-1 font-bold text-gray-800 dark:text-[#f0f6fc] bg-transparent"
                value={formData.siteTitle}
                onChange={(e) => handleChange("siteTitle", e.target.value)}
              />
            </div>
            <div className="flex items-center">
              <label className="w-40 text-sm font-bold text-gray-700 dark:text-[#848d97]"><T>Site Length</T></label>
              <input 
                type="number"
                disabled={readOnly}
                className="flex-1 border-b border-gray-300 dark:border-[#30363d] focus:border-[#D9531E] outline-none text-sm py-1 bg-transparent text-gray-900 dark:text-white"
                value={formData.siteLength}
                onChange={(e) => handleMeasurementChange("siteLength", e.target.value)}
              />
            </div>
         </div>
         <div className="space-y-4">
            <div className="flex items-center">
              <label className="w-32 text-sm font-bold text-gray-700 dark:text-[#848d97]"><T>Site Width</T></label>
              <input 
                type="number"
                disabled={readOnly}
                className="flex-1 border-b border-gray-300 dark:border-[#30363d] focus:border-[#D9531E] outline-none text-sm py-1 bg-transparent text-gray-900 dark:text-white"
                value={formData.siteWidth}
                onChange={(e) => handleMeasurementChange("siteWidth", e.target.value)}
              />
            </div>
            <div className="flex items-center">
              <label className="w-32 text-sm font-bold text-gray-700 dark:text-[#848d97]"><T>Site Area</T></label>
              <div className="flex-1 border-b border-gray-300 dark:border-[#30363d] text-sm py-1 font-bold text-gray-800 dark:text-[#f0f6fc] bg-gray-50 dark:bg-[#21262d] px-2 rounded-t">
                 {formData.siteArea ? Number(formData.siteArea).toLocaleString() : 0} sq.ft
              </div>
            </div>
         </div>
      </div>

      {/* --- SECTION 3: UNITS / SUB-PROJECTS TABLE --- */}
      <h3 className="text-xs font-bold text-gray-500 dark:text-[#848d97] uppercase tracking-wider mb-4 border-b border-gray-100 dark:border-[#30363d] pb-2"><T>Unit Costing Breakdown</T></h3>
      <div className="border border-gray-200 dark:border-[#30363d] rounded-xl overflow-hidden shadow-xs">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-100 dark:bg-[#21262d] text-xs font-bold text-gray-600 dark:text-[#848d97] uppercase border-b border-gray-200 dark:border-[#30363d]">
            <tr>
              <th className="px-4 py-3"><T>Product / Unit Name</T></th>
              <th className="px-4 py-3 w-24"><T>Qty</T></th>
              <th className="px-4 py-3 w-24"><T>UoM</T></th>
              <th className="px-4 py-3 text-right"><T>Pre-Con Cost</T></th>
              <th className="px-4 py-3 text-right"><T>Under-Con Cost</T></th>
              <th className="px-4 py-3 text-right"><T>Post-Con Cost</T></th>
              <th className="px-4 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-[#30363d]">
            {formData.subUnits.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-[#21262d] transition">
                <td className="px-4 py-2">
                  <input 
                    type="text" 
                    disabled={readOnly}
                    className="w-full bg-transparent outline-none border-b border-transparent focus:border-[#D9531E] text-gray-900 dark:text-white"
                    value={item.name}
                    placeholder="e.g. Shop A-101"
                    onChange={(e) => updateLine(item.id, 'name', e.target.value)}
                  />
                </td>
                <td className="px-4 py-2">
                  <input 
                    type="number" 
                    disabled={readOnly}
                    className="w-full bg-transparent outline-none text-right border-b border-transparent focus:border-[#D9531E] text-gray-900 dark:text-white"
                    value={item.qty}
                    onChange={(e) => updateLine(item.id, 'qty', e.target.value)}
                  />
                </td>
                <td className="px-4 py-2">
                  <select 
                    disabled={readOnly}
                    className="w-full bg-transparent outline-none text-xs text-gray-900 dark:text-white"
                    value={item.uom}
                    onChange={(e) => updateLine(item.id, 'uom', e.target.value)}
                  >
                    <option className="dark:bg-[#161b22]"><T>Units</T></option>
                    <option className="dark:bg-[#161b22]"><T>Sq.ft</T></option>
                    <option className="dark:bg-[#161b22]"><T>Floors</T></option>
                  </select>
                </td>
                <td className="px-4 py-2">
                  <input 
                    type="number" 
                    disabled={readOnly}
                    className="w-full bg-transparent outline-none text-right border-b border-transparent focus:border-[#D9531E] text-gray-900 dark:text-white"
                    value={item.preCost}
                    onChange={(e) => updateLine(item.id, 'preCost', e.target.value)}
                  />
                </td>
                <td className="px-4 py-2">
                  <input 
                    type="number" 
                    disabled={readOnly}
                    className="w-full bg-transparent outline-none text-right border-b border-transparent focus:border-[#D9531E] text-gray-900 dark:text-white"
                    value={item.underCost}
                    onChange={(e) => updateLine(item.id, 'underCost', e.target.value)}
                  />
                </td>
                <td className="px-4 py-2">
                  <input 
                    type="number" 
                    disabled={readOnly}
                    className="w-full bg-transparent outline-none text-right border-b border-transparent focus:border-[#D9531E] text-gray-900 dark:text-white"
                    value={item.postCost}
                    onChange={(e) => updateLine(item.id, 'postCost', e.target.value)}
                  />
                </td>
                <td className="px-4 py-2 text-center">
                  {!readOnly && (
                      <button onClick={() => removeLine(item.id)} className="text-gray-400 dark:text-[#6e7681] hover:text-red-500 transition">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                  )}
                </td>
              </tr>
            ))}
            {!readOnly && (
                <tr>
                <td colSpan="7" className="px-4 py-2.5">
                    <button 
                    onClick={addLine}
                    className="text-[#D9531E] dark:text-[#F5875E] text-xs font-bold uppercase hover:underline"
                    >
                    + <T>Add a line</T>
                    </button>
                </td>
                </tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}